import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { apiGet, apiPut, apiDelete } from '../../lib/api'
import { 
  User, 
  Mail, 
  MapPin, 
  Briefcase,
  Bell,
  Lock,
  LogOut,
  ChevronRight,
  Edit2,
  Save,
  X,
  Plus,
  Trash2
} from 'lucide-react'
import AddProfessionModal from '../../components/AddProfessionModal'

interface UserProfession {
  id: string
  professionId: string
  professionName: string
  state: string
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, logout, isLoggingOut } = useAuth()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [showAddProfessionModal, setShowAddProfessionModal] = useState(false)
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    state: '',
    location: '',
  })

  const { data: profileData } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: () => apiGet<{ user: any }>('/auth/me'),
  })

  const { data: professionsData } = useQuery({
    queryKey: ['/api/user/professions'],
    queryFn: () => apiGet<{ professions: UserProfession[] }>('/user/professions'),
  })

  const profile = profileData?.user || user
  const userProfessions = professionsData?.professions || []

  const updateMutation = useMutation({
    mutationFn: (data: typeof editForm) => apiPut('/auth/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] })
      setIsEditing(false)
    },
  })

  const removeProfessionMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/user/professions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
    },
  })

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleSave = () => {
    updateMutation.mutate(editForm)
  }

  const startEditing = () => {
    setEditForm({
      name: profile?.name || '',
      state: profile?.state || '',
      location: profile?.location || '',
    })
    setIsEditing(true)
  }

  const handleRemoveProfession = (id: string, name: string) => {
    if (window.confirm(`Remove ${name}?`)) {
      removeProfessionMutation.mutate(id)
    }
  }

  const settingsItems = [
    {
      icon: Bell,
      label: 'Notification Settings',
      description: 'Manage how you receive alerts',
      to: '/profile/notifications',
    },
    {
      icon: Lock,
      label: 'Change Password',
      description: 'Update your password',
      to: '/profile/password',
    },
  ]

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{profile?.name}</h1>
              <p className="text-gray-500">{profile?.email}</p>
              {profile?.role && profile.role !== 'user' && (
                <span className="inline-flex items-center px-2 py-0.5 mt-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {profile.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                </span>
              )}
            </div>
          </div>
          
          {!isEditing ? (
            <button
              onClick={startEditing}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={editForm.state}
                  onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Texas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Austin"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm text-gray-900">{profile?.email}</p>
              </div>
            </div>
            {profile?.state && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm text-gray-900">
                    {profile.location ? `${profile.location}, ${profile.state}` : profile.state}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Professions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">{userProfessions.length >= 2 ? 'Professions' : 'Profession'}</h2>
          </div>
          <button
            onClick={() => setShowAddProfessionModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {userProfessions.length === 0 ? (
          <div className="text-center py-6">
            <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-3">No professions added yet.</p>
            <button
              onClick={() => setShowAddProfessionModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Profession
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {userProfessions.map((prof) => (
              <div
                key={prof.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{prof.professionName}</p>
                  <p className="text-xs text-gray-500">{prof.state}</p>
                </div>
                <button
                  onClick={() => handleRemoveProfession(prof.id, prof.professionName)}
                  disabled={removeProfessionMutation.isPending}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
        {settingsItems.map((item) => {
          const Icon = item.icon
          return (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          )
        })}
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
      >
        <LogOut className="w-5 h-5" />
        {isLoggingOut ? 'Signing out...' : 'Sign Out'}
      </button>

      {/* Delete Account Link */}
      <p className="text-center text-sm text-gray-500">
        Need to delete your account?{' '}
        <Link to="/profile/delete" className="text-red-600 hover:underline">
          Delete account
        </Link>
      </p>

      <AddProfessionModal
        isOpen={showAddProfessionModal}
        onClose={() => setShowAddProfessionModal(false)}
      />
    </div>
  )
}
